import {useRef, useEffect, useCallback} from 'react';
import {Platform} from 'react-native';
import {useSelector} from 'react-redux';
import PlatformWorld from 'components/World/PlatformWorld';
import World from 'components/World';
import Appearances from 'utils/Appearances';
import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {TUTORS_ADDRESSABLES_CATALOG_BASE_URL} from 'config/app.config';
import {COMMAND_ROUTE} from 'utils/World/config';

/**
 * Canonical display name for the default LCV tutor.
 * Passes to Unity as lowercase snake_case (e.g. `benjamin_franklin`), matching
 * typical Addressable **addresses** (see tutors `catalog_view.json` — the
 * `m_InternalIds` list also mentions `*.prefab` paths; the runtime key is not
 * always identical to those strings).
 */
export const LCV_DEFAULT_AI_TEACHER_NAME = 'Benjamin Franklin';

function normalizeTutorKeyForAddressables(displayOrTokenName) {
  return String(displayOrTokenName || '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();
}

function resolveTutorsAddressablesCatalogUrl() {
  let base = '';
  try {
    base = getRemoteConfigValue(
      'tutors_addressables_catalog_base_url',
    ).asString();
  } catch (_) {
    base = '';
  }
  const trimmed = String(base || '').trim();
  const platform = Appearances.getAddressablesPlatformName();
  if (trimmed) {
    const withSlash = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    return `${withSlash}${platform}/catalog_view.json`;
  }
  const fallback = String(TUTORS_ADDRESSABLES_CATALOG_BASE_URL || 'https://tutors.quiklearn.ai/dev/').replace(
    /\/$/,
    '',
  );
  return `${fallback}/${platform}/catalog_view.json`;
}

/**
 * When there is no JWT / `courseContentUrl`, the command must still include a
 * non-empty first argument: Unity parses `setup_ai_tutor` by splitting on
 * spaces, so an empty URL shifts every argument (teacher name becomes "the URL").
 * This is a stable public unit JSON whose curriculum lists Benjamin Franklin as tutor.
 */
export const LCV_FALLBACK_COURSE_CONTENT_URL =
  'https://ql-unit-json.s3.us-west-2.amazonaws.com/Science/2S_P4.json';

/** ms: let `initialize_avatar_system` run before other Unity commands stack up. */
const LCV_DELAY_AFTER_AVATAR_INIT_MS = 200;
/** ms: tutors catalog + bundles load asynchronously; `setup_ai_tutor` must run after. */
const LCV_DELAY_BEFORE_SETUP_AI_TUTOR_MS = 2800;

/**
 * Mirrors the web branch of `World.onLevelLoadComplete` after Unity sends legacy route `x`
 * (level finished loading). Without this, the scene can stay on a default camera (e.g. sky only).
 */
export function useLessonViewerUnityOnLegacyMessage(locationFolder) {
  const initDone = useRef(false);
  const setupTutorTimeoutRef = useRef(null);
  const bridgePhaseTimeoutRef = useRef(null);
  const legacy = useSelector(s => s.meeting.meeting.legacy);
  const directLanding = useSelector(s => s.meeting.meeting.direct_landing);
  const newAvatarUrl = useSelector(s => s.meeting.meeting.avatarUrlValue ?? '');
  const courseContentUrl =
    useSelector(s => s.app?.courseTokenValues?.courseContentUrl) ?? '';
  const aiTeacherToLoad =
    useSelector(s => s.app?.courseTokenValues?.AITeacherToLoad) ?? '';
  const courseProgress =
    useSelector(s => s.app?.courseTokenValues?.courseProgress) ?? 0;
  const userId =
    useSelector(s => s.app?.courseTokenValues?.userId) ?? 'lesson_viewer';
  const collectEmails =
    useSelector(s => s.app?.courseTokenValues?.collectEmails) ?? false;

  useEffect(() => {
    initDone.current = false;
    return () => {
      if (setupTutorTimeoutRef.current) {
        clearTimeout(setupTutorTimeoutRef.current);
        setupTutorTimeoutRef.current = null;
      }
      if (bridgePhaseTimeoutRef.current) {
        clearTimeout(bridgePhaseTimeoutRef.current);
        bridgePhaseTimeoutRef.current = null;
      }
    };
  }, [locationFolder]);

  return useCallback(
    message => {
      if (Platform.OS !== 'web') {
        return;
      }
      if (!message || message.route !== 'x') {
        return;
      }
      if (initDone.current) {
        return;
      }
      initDone.current = true;

      const locFilter = locationFolder;

      const addressablesCatalog = resolveTutorsAddressablesCatalogUrl();
      World.runCommand(`initialize_avatar_system ${addressablesCatalog}`);

      if (bridgePhaseTimeoutRef.current) {
        clearTimeout(bridgePhaseTimeoutRef.current);
      }
      if (setupTutorTimeoutRef.current) {
        clearTimeout(setupTutorTimeoutRef.current);
      }

      bridgePhaseTimeoutRef.current = setTimeout(() => {
        bridgePhaseTimeoutRef.current = null;

        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: `set_location_filter ${locFilter}`,
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: 'finish_loading',
        });
        PlatformWorld.sendMessage({route: COMMAND_ROUTE, payload: 'after_join'});
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: 'show_names false',
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: 'show_chat false',
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: 'set_quality HIGH',
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: 'set_fps_limit 30',
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: `set_legacy ${legacy}`,
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: `direct_landing ${directLanding}`,
        });
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: `set_avatar_url ${newAvatarUrl} ${directLanding}`,
        });

        const tutorDisplayOrToken =
          typeof aiTeacherToLoad === 'string' && aiTeacherToLoad.trim() !== ''
            ? aiTeacherToLoad
            : LCV_DEFAULT_AI_TEACHER_NAME;
        const tutorKey = normalizeTutorKeyForAddressables(tutorDisplayOrToken);
        const effectiveCourseContentUrl =
          typeof courseContentUrl === 'string' && courseContentUrl.trim() !== ''
            ? courseContentUrl.trim()
            : LCV_FALLBACK_COURSE_CONTENT_URL;

        setupTutorTimeoutRef.current = setTimeout(() => {
          setupTutorTimeoutRef.current = null;
          World.runCommand(
            `setup_ai_tutor ${effectiveCourseContentUrl} ${tutorKey} ${courseProgress} ${userId} ${collectEmails}`,
          );
        }, LCV_DELAY_BEFORE_SETUP_AI_TUTOR_MS);
      }, LCV_DELAY_AFTER_AVATAR_INIT_MS);
    },
    [
      locationFolder,
      legacy,
      directLanding,
      newAvatarUrl,
      courseContentUrl,
      aiTeacherToLoad,
      courseProgress,
      userId,
      collectEmails,
    ],
  );
}
